import { StowageRuleContext } from '../../../types/ViewModel/Stowage/StowageRule/StowageRuleContext';
import { StowageRulePropertyContext } from '../../../types/ViewModel/Stowage/StowageRule/StowageRulePropertyContext';
import { StowageRuleTestScope } from 'src/enums/stowage/stowageRule/stowageRuleTestScope';
import { StowageRuleTestLeafBM } from 'src/types/BusinessModel/Stowage/StowageRule/StowageRuleTestLeafBM';
import { PropertyManager } from './propertyManager';
import { StowageRulePropertyBM } from 'src/types/BusinessModel/Stowage/StowageRule/StowageRulePropertyBM';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { StowageRulePropertyOperator } from 'src/enums/stowage/stowageRule/stowageRulePropertyOperator';
import { RotationCargo } from 'src/types/ViewModel/Stowage/Rotation/RotationCargo';
import { StowageRulePropertyLocation } from 'src/enums/stowage/stowageRule/stowageRulePropertyLocation';
import { roundTo3Decimals } from 'src/utils/numberHelper';
import { TankContent } from 'src/types/ViewModel/Stowage/Manifest/TankContent';
import { StowageDataAccessor } from './stowageDataAccessor';

const logLeafManager: boolean = false;

export class LeafManager {
    private propertyManager: PropertyManager;

    constructor(dataAccessor: StowageDataAccessor, propertyManager: PropertyManager) {
        this._dataAccessor = dataAccessor;
        this.propertyManager = propertyManager;
    }

    private _dataAccessor: StowageDataAccessor;

    public processLeaf(leaf: StowageRuleTestLeafBM, stowageRuleContext: StowageRuleContext): boolean {
        let result = false;

        // Early return for invalid properties
        if (!leaf.properties || leaf.properties.length === 0) {
            return false;
        }

        switch (leaf.scope) {
            case StowageRuleTestScope.AllAdjacentTanks:
            case StowageRuleTestScope.AnyAdjacentTanks:
                result = this.processAdjacentTanks(leaf.scope, stowageRuleContext, leaf);
                break;
            case StowageRuleTestScope.Tank: {
                if (!stowageRuleContext.tank) return false;

                if (logLeafManager) console.log(`Processing tank ${stowageRuleContext.tank.tankId} ${stowageRuleContext.tank.tankName}`);

                // Pre-allocate array for better memory usage
                const propertyValues = new Array(leaf.properties.length);
                for (let i = 0; i < leaf.properties.length; i++) {
                    propertyValues[i] = this.getPropertyValue(
                        stowageRuleContext,
                        leaf.properties[i],
                        stowageRuleContext.tank,
                        stowageRuleContext.cargo,
                        null
                    );
                }
                result = this.processLeafProperties(leaf.operator, leaf.margin, propertyValues);
                break;
            }
            case StowageRuleTestScope.Ship:
            case StowageRuleTestScope.Cargo:
            case StowageRuleTestScope.CargoOperation: {
                if (logLeafManager) console.log(`Processing ship/cargo/cargo operation rule`);

                // Pre-allocate array for better memory usage
                const propertyValues = new Array(leaf.properties.length);
                for (let i = 0; i < leaf.properties.length; i++) {
                    propertyValues[i] = this.getPropertyValue(stowageRuleContext, leaf.properties[i], null, stowageRuleContext.cargo, null);
                }
                result = this.processLeafProperties(leaf.operator, leaf.margin, propertyValues);
                stowageRuleContext.applyConsequenceToAlltanks = true;
                break;
            }
        }

        if (logLeafManager) console.log(`Leaf evaluation result: ${result}`);
        return result;
    }

    private processAdjacentTanks = (testScope: StowageRuleTestScope, stowageRuleContext: StowageRuleContext, leaf: StowageRuleTestLeafBM) => {
        const adjacentTanks = stowageRuleContext.tank.adjacentTanks?.map((tank: any) => tank.tank);
        if (!adjacentTanks || adjacentTanks.length === 0) {
            if (logLeafManager) console.log('No adjacent tanks found.');
            return false;
        }

        let result = testScope === StowageRuleTestScope.AllAdjacentTanks;
        let adjacentCargoFound = false;

        for (const adjacentTank of adjacentTanks) {
            if (logLeafManager) console.log(`Processing adjacent tank ${adjacentTank.tankId} ${adjacentTank.tankName}`);
            const adjacentTankContent = this._dataAccessor.getTankContent(stowageRuleContext, adjacentTank);
            const adjacentCargoes = stowageRuleContext.operationTankContents
                ? adjacentTankContent?.cargoContents?.filter((content) => content.weight.gt(0)).map((content) => content.cargo) ?? []
                : [adjacentTankContent?.cargoContents?.[0]?.cargo];
            for (const adjacentCargo of adjacentCargoes) {
                if (adjacentCargo && adjacentCargo.cargoGuid !== stowageRuleContext.cargo.cargoGuid) {
                    adjacentCargoFound = true;
                    const propertyValues = leaf.properties.map((property) => {
                        return property.location === StowageRulePropertyLocation.Tank
                            ? this.getPropertyValue(stowageRuleContext, property, stowageRuleContext.tank, stowageRuleContext.cargo, null)
                            : this.getPropertyValue(stowageRuleContext, property, adjacentTank, adjacentCargo, adjacentTankContent);
                    });
                    if (testScope === StowageRuleTestScope.AllAdjacentTanks) {
                        result = result && this.processLeafProperties(leaf.operator, leaf.margin, propertyValues);
                    } else {
                        const adjacentTankResult = this.processLeafProperties(leaf.operator, leaf.margin, propertyValues);
                        if (adjacentTankResult && !stowageRuleContext.adjacentTanks.includes(adjacentTank)) {
                            stowageRuleContext.adjacentTanks.push(adjacentTank);
                        }
                        result = result || adjacentTankResult;
                    }
                }
            }
        }
        if (!adjacentCargoFound) {
            // If no adjacent cargo found (either no cargo or all same cargo):
            // - AllAdjacentTanks: return true (vacuous truth - all adjacent tanks satisfy condition)
            // - AnyAdjacentTanks: return false (no adjacent tanks satisfy condition)
            return testScope === StowageRuleTestScope.AllAdjacentTanks;
        }
        return result;
    };

    private getPropertyValue(
        stowageRuleContext: StowageRuleContext,
        property: StowageRulePropertyBM,
        tank: Tank,
        cargo: RotationCargo,
        adjacentTankContent: TankContent
    ): any {
        const propertyContext: StowageRulePropertyContext = {
            dataAccessor: this._dataAccessor,
            berthCall: stowageRuleContext.berthCall,
            tankContent: stowageRuleContext.tankContent,
            adjacentTankContent: adjacentTankContent,
            tank: tank,
            cargo: cargo,
            cargoOperation: stowageRuleContext.cargoOperation,
        };
        const value = this.propertyManager.getPropertyValue(propertyContext, property);
        stowageRuleContext.ruleEvidence?.push(JSON.stringify([property.fieldPath ?? property.name, tank?.tankId, cargo?.cargoGuid, value]));
        return value;
    }

    private processLeafProperties(operator: StowageRulePropertyOperator, margin: number, propertyValues: any[]): boolean {
        if (!Array.isArray(propertyValues) || propertyValues.length === 0) {
            console.error('propertyValues must be a non-empty array.');
            return false;
        }
        if (
            operator === StowageRulePropertyOperator.IsLessThan ||
            operator === StowageRulePropertyOperator.IsGreaterThan ||
            operator === StowageRulePropertyOperator.Contains ||
            operator === StowageRulePropertyOperator.DoesNotContain
        ) {
            if (propertyValues.length < 2) {
                console.error(
                    `Operator ${StowageRulePropertyOperator[operator]} requires exactly two properties. Received ${propertyValues.length}.`
                );
                return false;
            }
        }
        if (operator === StowageRulePropertyOperator.Contains || operator === StowageRulePropertyOperator.DoesNotContain) {
            if (!Array.isArray(propertyValues[0]) || typeof propertyValues[1] !== 'string') {
                console.error(
                    `Operator ${StowageRulePropertyOperator[operator]} requires values of type Array and string. Received: ${
                        propertyValues[0]
                    } (${typeof propertyValues[0]}) and ${propertyValues[1]}  (${typeof propertyValues[1]})`
                );
                return false;
            }
        }

        let result: boolean = false;
        switch (operator) {
            case StowageRulePropertyOperator.Equals:
                result = propertyValues.every((value) => value === propertyValues[0]);
                break;
            case StowageRulePropertyOperator.IsNotEqualTo:
                result = propertyValues.some((value) => value !== propertyValues[0]);
                break;
            case StowageRulePropertyOperator.IsGreaterThan:
                result = roundTo3Decimals(propertyValues[0]) > roundTo3Decimals(propertyValues[1] + (margin ? margin : 0));
                break;
            case StowageRulePropertyOperator.IsGreaterOrEqualThan:
                result = roundTo3Decimals(propertyValues[0]) >= roundTo3Decimals(propertyValues[1] + (margin ? margin : 0));
                break;
            case StowageRulePropertyOperator.IsLessThan:
                result = roundTo3Decimals(propertyValues[0]) < roundTo3Decimals(propertyValues[1] + (margin ? margin : 0));
                break;
            case StowageRulePropertyOperator.IsLessOrEqualThan:
                result = propertyValues[0] <= propertyValues[1] + (margin ? margin : 0);
                break;
            case StowageRulePropertyOperator.Contains:
                result = propertyValues[0].includes(propertyValues[1]);
                break;
            case StowageRulePropertyOperator.DoesNotContain:
                result = !propertyValues[0].includes(propertyValues[1]);
                break;
            default:
                console.error(`Unsupported operator: ${operator}`);
        }
        if (logLeafManager) console.log(`Properties evaluation result: ${StowageRulePropertyOperator[operator]}(${propertyValues}) = ${result}`);
        return result;
    }
}
